import React, { useState } from 'react';
import { connect } from 'react-redux';
import { getFormValues, change, isDirty } from 'redux-form';
import ApplyPromotionModal from './promotions/ApplyPromotionModal';
import AddWarrantyModal from './warranty/AddWarrantyModal';
import CurrencyTypeView from '../../../../itrust_common/components/others/CurrencyTypeView';
import UnFinalizeButton from '../moreActions/UnFinalizeButton';
import ClaimFormDropdownButton from '../ClaimFormDropdownButton';
import FinalizeButton from '../moreActions/FinalizeButton';
import { ExportButton } from '../../../../itrust_common/components/others';
import DuplicateOrderButton from '../../buttons/DuplicateOrderButton';
import DeleteOrderButton from '../../buttons/DeleteOrderButton';
import { orderInvoiceEmail, orderInvoicePrint, orderUpdate } from '../../../../redux/actions/order/orderActions';
import CurrencyIconsView from '../../../../itrust_common/components/others/CurrencyIconsView';
import EditExamButton from '../../buttons/EditExamButton';
import PaymentsButton from '../../order/moreActions/PaymentsButton';
import RefundsButton from '../../order/moreActions/RefundsButton';
import { printPatientStatement } from '../../../../redux/actions/paymentReport/otherPaymentReportActions';
import PatientStatementButton from '../../buttons/PatientStatementButton';
import Alert from '../../../../itrust_common/components/Alert';

function OrderHeader(props) {
  const {
    order,
    onRemoteSubmit,
    accordionToggle,
    setAccordionToggle,
    handleOrderEditCancel,
    activeTab,
    handleTabChange,
    onOpenClaimFormModal,
    dispatch,
    pdConfig,
    transactionNumberConfig,
    headerFormValues,
    isHeaderFormDirty,
    eyeglassFormValues,
  } = props;
  const [isPromoModalOpen, setIsPromoModalOpen] = useState(false);
  const [isWarrantyModalOpen, setIsWarrantyModalOpen] = useState(false);

  const getBalanceDueBgColor = () => {
    if (order.balance_due > 0) {
      return 'bg-danger'
    }
    else if (order.balance_due < 0) {
      return 'bg-success'
    }
    else {
      return 'bg-dark'
    }
  }

  const getTotalPaidBgColor = () => {
    if (order.amount_paid > 0) {
      return 'bg-success'
    }
    else {
      return 'bg-dark'
    }
  }

  const handleOnSave = () => {
    const transactionNumber =
      headerFormValues?.transaction_number ?? order?.transaction_number ?? '';
    const txnRequired =
      Boolean(transactionNumberConfig.enabled && transactionNumberConfig.required) &&
      !String(transactionNumber).trim();

    if (txnRequired) {
      Alert.warning('Please fill Transaction / Register # before saving the order.', null, false);
      return;
    }

    if (transactionNumberConfig.enabled) {
      dispatch(
        orderUpdate(
          { id: order.id, transaction_number: transactionNumber },
          { include: '*' },
        ),
      );
    }

    switch (activeTab) {
      case 1: onRemoteSubmit('orderProcedureForm'); break
      case 2: onRemoteSubmit('orderContactLensForm'); break
      case 3: onRemoteSubmit('orderEyeglassForm'); break
      case 4: onRemoteSubmit('orderMiscItemForm'); break
      default:
    }
  }

  const handleEmailInvoice = () => {
    dispatch(orderInvoiceEmail(order.id))
  }

  const handlePrintInvoice = async () => {
    let res = await dispatch(orderInvoicePrint(order.id))
    window.open(res.value.data.filepath, '_blank')
  }

  const handlePatientStatementPrint = async (id) => {
    const res = await dispatch(printPatientStatement(id))
    window.open(res?.value?.data?.filepath, '_blank')
  }

  const handleApplyPromotion = () => {
    setIsPromoModalOpen(true);
  };

  const handleApplyPromotionToOrder = (promo) => {
    if (!promo) return;
    dispatch(change('orderEyeglassForm', 'applied_promotion', promo));

    // Switch to Eyeglass tab (tab 3) if not active so user sees the banner and updated pricing
    if (handleTabChange && activeTab !== 3) {
      handleTabChange(3);
    }
  };

  const handleFinalizeClick = (e) => {
    if (!pdConfig || !eyeglassFormValues?.eyeglasses_attributes) return;

    let hasError = false;
    for (let eyeglass of eyeglassFormValues.eyeglasses_attributes) {
      if (eyeglass._destroy) continue;
      const pd = eyeglass.pupillary_distance || {};
      const fields = [
        'od_distance', 'od_intermediate', 'od_near',
        'os_distance', 'os_intermediate', 'os_near',
        'ou_distance', 'ou_intermediate', 'ou_near'
      ];
      for (let field of fields) {
        if (pdConfig[field]?.is_required && !pd[field]) {
          hasError = true;
          break;
        }
      }
      if (hasError) break;
    }

    if (hasError) {
      e.stopPropagation();
      Alert.warning("Please fill all required Pupillary Distance fields in the Eyeglass Order.");
    }
  }

  return (
    <div className="d-flex flex-row justify-content-between align-items-center text-nowrap">
      <div className="d-flex align-items-start">
        <div>
          <h5 className="mr-2">Patient Order Form</h5>
          <span className="ml-1"><strong>ID:</strong> {order.id}</span>
        </div>
        <a className="collapsed border border-primary rounded-circle ml-2" onClick={() => setAccordionToggle(!accordionToggle)} role="button" data-toggle="collapse" data-parent="#accordion" href="#collapseOne" aria-expanded="true" aria-controls="collapseOne">
          <i className="la la-sm la-angle-down text-primary px-2 pt-2" />
        </a>
        <div className='ml-4 align-self-center'>
          <span className={`rounded text-white mr-5 py-3 px-2 font-size-15 ${getBalanceDueBgColor()}`}>Balance Due: <CurrencyTypeView amount={order.balance_due} /></span>
          <span className={`rounded text-white py-3 px-2 font-size-15 mr-5 ${getTotalPaidBgColor()}`}>Total Paid: <CurrencyTypeView amount={order.amount_paid} /></span>
          <span className={`rounded text-white py-3 px-2 font-size-15 ${getTotalPaidBgColor()}`}>Patient Credit: <CurrencyTypeView amount={order?.patient_attributes?.patient_credits} /></span>
          {((order.write_off && order?.patient_insurance_attributes?.id) || order.cash_write_off) ? <>
            <span className={`rounded bg-info py-3 px-5 ml-5`}>
              Write Off: <CurrencyTypeView amount={(order.write_off && order?.patient_insurance_attributes?.id) ? order.write_off : order.cash_write_off} />
            </span>
          </> : ''}
        </div>
      </div>
      <div className='d-flex align-items-center'>
        {order.status == 'finalized' && order.orders_insurances_attributes?.length > 0 && <ClaimFormDropdownButton order={order} dispatch={dispatch} onOpenClaimFormModal={onOpenClaimFormModal} />}
        {order.exam_examination_id &&
          <EditExamButton storeId={order.store_id} examId={order.exam_examination_id}>
            <button type="button" className="btn btn-outline-primary mx-lg-3">Open Exam</button>
          </EditExamButton>
        }
        <button className="btn btn-outline-primary mx-lg-3" onClick={handleOnSave}>Save Order</button>
        {order.status === "finalized" &&
          <UnFinalizeButton appointmentId={order.appointment_id} orderId={order.id}>
            <button
              type="button"
              className="btn btn-warning mx-lg-3">
              Unfinalize and Edit
            </button>
          </UnFinalizeButton>
        }
        {order.status === "finalized" &&
          <button
            type="button"
            className="btn btn-success mx-lg-3">
            Finalized
          </button>
        }
        {(order.balance_due || order.status !== "finalized") ?
          <span onClickCapture={handleFinalizeClick}>
            <FinalizeButton showRequiredAuth={order.patient_insurance_attributes?.is_prior_authorization_required && !order.prior_authorization_number} orderStaffId={order.staff_attributes?.id} balanceDue={order.balance_due} onFinalize={handleOnSave} orderId={order.id} patientName={order.patient_attributes?.name}>
              <button
                type="button"
                className="btn btn-primary mx-lg-3">
                {order.balance_due ? 'Pay' : 'Finalize Order'}
              </button>
            </FinalizeButton>
          </span> : ''
        }
        <div className="dropdown d-inline-block mx-3 bg-light">
          <button className="btn btn-primary dropdown-toggle" type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
            More Actions
            <i className="ml-3 la la-angle-down la-sm" />
          </button>
          <div className="dropdown-menu border-0 shadow rounded my-3" aria-labelledby="dropdownMenuButton">
            <RefundsButton orderId={order.id}>
              <a className="dropdown-item d-flex align-item-center border-bottom border-bottom-2 border-light py-3" href="#">
                <i className="la la-sm mr-3 la-history" />
                Refund
              </a>
            </RefundsButton>
            <PatientStatementButton onClick={() => handlePatientStatementPrint(order.id)} >
              <a className="dropdown-item d-flex align-items-center border-bottom border-bottom-2 border-light py-3 "><i className="la la-file-alt la-sm mr-3" />Patient Statement</a>
            </PatientStatementButton>
            <button
              type="button"
              className="dropdown-item d-flex align-items-center border-bottom border-bottom-2 border-light py-3 pointer"
              onClick={handleApplyPromotion}
            >
             <i className="la la-sm la-percent mr-3" />
              Apply Promotion 
            </button>
            <button
              type="button"
              className="dropdown-item d-flex align-items-center border-bottom border-bottom-2 border-light py-3 pointer"
              onClick={() => setIsWarrantyModalOpen(true)}
            >
              <i className="la la-sm la-shield-alt mr-3" />
              Add Warranty
            </button>
            <PaymentsButton orderId={order.id}>
              <a className="dropdown-item d-flex align-item-center border-bottom border-bottom-2 border-light py-3" href="#">
                <CurrencyIconsView className="mr-4" />
                Payment Details
              </a>
            </PaymentsButton>
            <a className="dropdown-item d-flex align-item-center border-bottom border-bottom-2 border-light py-3" onClick={handleEmailInvoice}>
              <i className="la la-sm mr-3 la-paper-plane" />
              Email Invoice
            </a>
            <a className="dropdown-item d-flex align-item-center border-bottom border-bottom-2 border-light py-3 pointer" onClick={handlePrintInvoice}>
              <i className="la la-sm mr-3 la-print" />
              Print Invoice
            </a>

            <ExportButton
              path={`/v1/orders/${order.id}/download_invoice_pdf`}
              label={<span><i className="la la-sm mr-3 la-edit" />Download Invoice</span>}
              className="dropdown-item d-flex align-item-center border-bottom border-bottom-2 border-light rounded-0"
            />
            <DuplicateOrderButton orderId={order.id} >
              <a className="dropdown-item d-flex align-items-center py-3"><i className="la la-copy la-sm mr-3" />Duplicate</a>
            </DuplicateOrderButton>
            <DeleteOrderButton order={order} onDeleteHook={handleOrderEditCancel}>
              <a className="dropdown-item d-flex align-items-center text-danger py-3"><i className="la la-trash la-sm mr-3" />Delete</a>
            </DeleteOrderButton>
          </div>
        </div>
        <button className="btn btn-outline-primary mx-3" data-dismiss="modal" onClick={handleOrderEditCancel}>Close</button>
      </div>
      <ApplyPromotionModal
        isOpen={isPromoModalOpen}
        onClose={() => setIsPromoModalOpen(false)}
        onApply={handleApplyPromotionToOrder}
        order={order}
      />
      <AddWarrantyModal
        isOpen={isWarrantyModalOpen}
        onClose={() => setIsWarrantyModalOpen(false)}
        order={order}
      />
    </div>
  )
}

const mapStateToProps = (state) => {
  const templates = state.setting?.template?.templates || [];
  const ordersTemplateConfig = templates.find((t) => t.category === 'orders')?.configuration || {};

  return {
    pdConfig: ordersTemplateConfig?.eyeglass?.pupilary_distance,
    transactionNumberConfig: ordersTemplateConfig?.transaction_number || {},
    headerFormValues: getFormValues('orderHeaderForm')(state),
    isHeaderFormDirty: isDirty('orderHeaderForm')(state),
    eyeglassFormValues: getFormValues('orderEyeglassForm')(state),
  };
};

export default connect(mapStateToProps)(OrderHeader);
